import Button from "components/atoms/Button";
import InputDate from "components/atoms/InputDate";
import InputSelect from "components/atoms/InputSelect";
import TableDataComponent from "components/organisms/TableData";
import moment from "moment";
import React from "react";
import { CheckSquare, XSquare } from "react-feather";
import { useState } from "react";
import { useSelector } from "react-redux";
import { MonthOptions, YesAndNoOptions } from "utils/constant";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import ActionButton from "components/atoms/ActionButton";
import { useEffect } from "react";
import UjiSilangService from "services/UjiSilang";
import { errorToastNotif, successToastNotif } from "utils/common";
import InputField from "components/atoms/InputField";
import InputRadio from "components/atoms/InputRadio";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import { maskNumber } from "utils/helpers";

const ujiSilangService = new UjiSilangService();

// Standarisasi kode slide negatif mengikuti format prefix Regmal 1:
// Kode Kabko/Kode Faskes/SN-(Nomor Urut)/Bulan/Tahun
// Contoh: 1101/1010001/SN-Knowlesi001/06/2026

// Membangun prefix kode slide: Kode Kabko/Kode Faskes/SN-
const buildSlidePrefix = (row) => {
  const kabko = row?.district_id?.split("|")[0] || "";
  const faskes = row?.fasyankes_id?.split("|")[0] || "";
  return `${kabko}/${faskes}/SN-`;
};

// Membangun suffix kode slide: /Bulan/Tahun
const buildSlideSuffix = (row) => {
  const month = String(row?.month ?? "").padStart(2, "0");
  const year = row?.year ?? "";
  return `/${month}/${year}`;
};

// Menggabungkan prefix + nomor urut + suffix menjadi kode slide lengkap
const composeSlideCode = (row, nomorUrut) => {
  return `${buildSlidePrefix(row)}${(nomorUrut ?? "").trim()}${buildSlideSuffix(
    row,
  )}`;
};

// Validasi kualitas data Kode Slide Negatif: bagian Nomor Urut (bagian bebas
// yang diinput pengguna) hanya boleh berisi huruf dan angka. Karakter khusus
// seperti ' " * / \ dan karakter khusus lainnya tidak diperbolehkan.
const ALLOWED_NOMOR_URUT_PATTERN = /^[A-Za-z0-9]+$/;

// Membersihkan input Nomor Urut dari karakter yang tidak diperbolehkan
// sehingga karakter khusus tidak dapat diketik/di-paste ke dalam field.
const sanitizeNomorUrut = (value) =>
  String(value ?? "").replace(/[^A-Za-z0-9]/g, "");

// Mengekstrak nomor urut dari kode slide lengkap (mode edit data lama)
// Dibuat toleran terhadap data lama yang formatnya belum sepenuhnya sesuai
// standar baru (mis. bulan belum di-pad "6" vs "06", atau prefix berbeda),
// agar tidak terjadi pembungkusan ganda saat disimpan ulang.
const extractNomorUrut = (row) => {
  const code = row?.slide_code || "";
  if (!code) return "";

  // Prioritas: cari segmen yang mengandung penanda "SN-" lalu ambil nomor
  // urut setelahnya. Ini menangani data lama walau bulan/tahun belum pas.
  const parts = code.split("/");
  const snPart = parts.find((p) => p.includes("SN-"));
  if (snPart) {
    return snPart.split("SN-")[1] || "";
  }

  // Fallback: strip prefix & suffix secara persis jika penanda "SN-"
  // tidak ditemukan.
  const prefix = buildSlidePrefix(row);
  const suffix = buildSlideSuffix(row);
  let value = code;
  if (prefix && value.startsWith(prefix)) {
    value = value.slice(prefix.length);
  }
  if (suffix && value.endsWith(suffix)) {
    value = value.slice(0, value.length - suffix.length);
  }

  // Jika masih mengandung "/" berarti format lama tidak dikenali; kembalikan
  // kosong agar pengguna mengisi ulang nomor urut dengan format yang bersih.
  return value.includes("/") ? "" : value;
};

const Table = () => {
  const [filterState, setFilterState] = useState({
    filter_year: "",
    filter_month: "",
    filter_name: "",
  });
  const [defaultData, setDefaultData] = useState([]);
  const [dataState, setDataState] = useState({
    page: 1,
    limit: 10,
    data: [],
    totalData: 0,
    currentPage: 1,
    nextPage: null,
    previousPage: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [jumlahSd, setJumlahSd] = useState(1);
  const [petugasOptions, setPetugasOptions] = useState([]);

  const { currentUser } = useSelector((state) => state.authReducer);
  const fasyankeId = currentUser?.fasyankes_id;

  const fetchPetugas = async () => {
    try {
      const response = await ujiSilangService.petugasMikroskopis({
        page: 1,
        limit: 1000,
        searchBy: `kd_faskes`,
        searchValue: `${fasyankeId?.split("|")[0]}`,
      });
      setPetugasOptions(
        (response.petugas ?? []).map((o) => ({
          label: `${o.name} - ${maskNumber(o.nik)}`,
          value: `${o.name} - ${o.nik}`,
        })),
      );
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPetugas();
  }, []);

  const fetchInputData = async () => {
    if (!filterState.filter_year || !filterState.filter_month) return;
    setIsLoading(true);
    try {
      const { filter_month, filter_year, filter_name } = filterState;
      const response = await ujiSilangService.slideGetAll({
        page: dataState.page,
        limit: dataState.limit,
        searchBy: `fasyankes_id,year,month,slide,name`,
        searchValue: `${fasyankeId},${filter_year},${filter_month},negatif,${filter_name}`,
      });

      const data = (response.slide || []).map((o) => ({
        ...o,
        isEdit: false,
        saved_is_uji_silang_kabkota: o.is_uji_silang_kabkota || "",
        saved_tgl_slide_dikirim: o.tgl_slide_dikirim || "",
      }));

      setDefaultData(JSON.parse(JSON.stringify(data)));
      setDataState((prevState) => ({
        ...prevState,
        data,
        limit: response.limit,
        currentPage: response.currentPage,
        totalData: response.total,
        nextPage: response.nextPage,
        previousPage: response.previousPage,
      }));
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInputData();
  }, [dataState.page]);

  const generateData = () => {
    const listData = [];
    for (let i = 0; i < jumlahSd; i++) {
      listData.push({
        id: uuidv4(),
        province_id: currentUser?.province_id,
        district_id: currentUser?.district_id,
        fasyankes_id: currentUser?.fasyankes_id,
        year: filterState.filter_year,
        month: filterState.filter_month,
        name: "",
        slide_code: "",
        parasyte: [],
        is_uji_silang_kabkota: "",
        tgl_slide_dikirim: "",
        petugas_mikroskopis: "",
        isEdit: true,
      });
    }
    setDefaultData(JSON.parse(JSON.stringify(listData)));
    setDataState((prev) => ({
      ...prev,
      data: listData,
      limit: jumlahSd,
      currentPage: 1,
      totalData: jumlahSd,
      nextPage: null,
      previousPage: null,
    }));
  };

  const onPageChange = (page) => {
    setDataState((prevState) => ({
      ...prevState,
      page: page,
    }));
  };

  const onSaveData = async (rowIndex, item) => {
    let errorMessage = "";
    const nomorUrut = extractNomorUrut(item)?.trim();
    // Selalu compose ulang slide_code ke format baru dari nomor urut yang
    // diekstrak. Ini memastikan data lama (mis. hanya "001") tetap tersimpan
    // dengan format lengkap walaupun pengguna tidak menyentuh input kode slide.
    const normalizedSlideCode = nomorUrut
      ? composeSlideCode(item, nomorUrut)
      : item.slide_code;
    item.slide_code = normalizedSlideCode;
    const normalizedCode = normalizedSlideCode?.trim()?.toLowerCase();

    if (!item.name) {
      errorMessage = "Nama harus diisi";
    } else if (!nomorUrut) {
      errorMessage = "Nomor urut kode slide harus diisi";
    } else if (!ALLOWED_NOMOR_URUT_PATTERN.test(nomorUrut)) {
      errorMessage =
        "Kode Slide Negatif tidak boleh mengandung karakter khusus (contoh: ' \" * / \\). Nomor urut hanya boleh berisi huruf dan angka.";
    } else if (
      dataState.data.some(
        (d, idx) =>
          idx !== rowIndex &&
          d.slide_code?.trim()?.toLowerCase() === normalizedCode
      )
    ) {
      errorMessage = "Kode slide sudah digunakan, tidak boleh duplikat";
    }

    if (!errorMessage) {
      try {
        const { is_duplicate } = await ujiSilangService.checkSlideCodeDuplicate({
          slide_code: item.slide_code,
          fasyankes_id: item.fasyankes_id,
          year: item.year,
          month: item.month,
          slide: "negatif",
          exclude_id: item.id,
        });

        if (is_duplicate) {
          errorMessage = "Kode slide sudah digunakan, tidak boleh duplikat";
        }
      } catch (err) {
        console.error(err);
        errorMessage = "Gagal memvalidasi kode slide";
      }
    }

    if (!errorMessage) {
      if (!item.is_uji_silang_kabkota) {
        errorMessage =
          "Dikirimkan untuk uji silang ke kab/kota tidak boleh kosong";
      } else if (
        item.is_uji_silang_kabkota === "Ya" &&
        !item.tgl_slide_dikirim
      ) {
        errorMessage = "Tanggal slide dikirim harus diisi";
      } else if (
        item.is_uji_silang_kabkota === "Ya" &&
        !item.petugas_mikroskopis
      ) {
        errorMessage = "Petugas mikroskopis harus diisi";
      }
    }

    if (errorMessage) {
      return toast.error(errorMessage, {
        position: "top-right",
        autoClose: 10000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }

    delete item.isEdit;

    await ujiSilangService
      .slideUpdate(
        {
          ...item,
          parasyte: [],
          slide: "negatif",
        },
        item.id,
      )
      .then(() => {
        successToastNotif("Data berhasil diperbarui");
        setDataState((prevState) => {
          const newData = [...prevState.data];
          newData[rowIndex].isEdit = false;
          newData[rowIndex].saved_is_uji_silang_kabkota = item.is_uji_silang_kabkota;
          newData[rowIndex].saved_tgl_slide_dikirim = item.tgl_slide_dikirim || "";
          return { ...prevState, data: newData };
        });
      })
      .catch((err) => {
        console.log(err);
        errorToastNotif(err);
      });
  };

  const columns = [
    {
      header: "Aksi",
      accessorKey: "action",
      size: 100,
      cell: ({ row: { index, original }, table }) => {
        const oldData = defaultData.find((o) => o.id === original.id);
        // Data locking: slide yang sudah diverifikasi Kab/Kota (hasil_kabkota
        // terisi) menjadi read only, sehingga tombol Edit disembunyikan.
        const isLocked = Boolean(original.hasil_kabkota);
        return (
          <>
            <div className="button-actions">
              {original.isEdit ? (
                <>
                  <ActionButton
                    onClick={() => onSaveData(index, original)}
                    btnType={"save"}
                  />
                  <ActionButton
                    onClick={() => {
                      table.options.meta?.updateData(
                        index,
                        "isEdit",
                        false,
                        JSON.parse(JSON.stringify(oldData)),
                      );
                    }}
                    btnType={"cancel"}
                  />
                </>
              ) : isLocked ? null : (
                <ActionButton
                  onClick={() => {
                    table.options.meta?.updateData(index, "isEdit", true);
                  }}
                  btnType={"edit"}
                />
              )}
            </div>
            {!original.isEdit &&
            (original.saved_is_uji_silang_kabkota === "Ya" &&
            original.saved_tgl_slide_dikirim ? (
              <div
                style={{
                  color: "#198754",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 8,
                }}
              >
                <CheckSquare size={18} />
                Sudah Dikirim
              </div>
            ) : original.saved_is_uji_silang_kabkota === "Tidak" ? (
              <div
                style={{
                  color: "#dc3545",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 8,
                }}
              >
                <XSquare size={18} />
                Tidak Dikirim
              </div>
            ) : null)}
          </>
        );
      },
    },
    {
      header: "Nama Pasien",
      accessorKey: "name",
      cell: ({ row: { index, original }, table }) => {
        const [value, setValue] = useState(original.name || "");
        if (!original?.isEdit) return value;
        return (
          <InputField
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={(e) => {
              table.options.meta?.updateData(index, "name", e.target.value);
            }}
          />
        );
      },
    },
    {
      header: "Kode slide",
      accessorKey: "slide_code",
      cell: ({ row: { index, original }, table }) => {
        // Prefix & suffix dibuat otomatis mengikuti format Regmal 1.
        // Pengguna hanya menginput nomor urut kode slide.
        const prefix = buildSlidePrefix(original);
        const suffix = buildSlideSuffix(original);
        const [nomorUrut, setNomorUrut] = useState(extractNomorUrut(original));
        if (!original?.isEdit) return original.slide_code || "";
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              flexWrap: "nowrap",
            }}
          >
            <span
              style={{
                whiteSpace: "nowrap",
                color: "#6c757d",
                fontSize: 13,
              }}
            >
              {prefix}
            </span>
            <div style={{ minWidth: 130 }}>
              <InputField
                value={nomorUrut}
                placeholder={"Nomor urut"}
                // Hanya update state lokal saat mengetik agar tabel tidak
                // re-render setiap keystroke (mencegah input kehilangan focus
                // ketika menambah/menghapus karakter). Input dibersihkan dari
                // karakter khusus sehingga hanya huruf dan angka yang diterima.
                onChange={(e) => setNomorUrut(sanitizeNomorUrut(e.target.value))}
                // Komposisi slide_code lengkap dilakukan saat blur, sehingga
                // data yang tersimpan selalu format kode slide yang benar.
                onBlur={(e) => {
                  const cleaned = sanitizeNomorUrut(e.target.value);
                  setNomorUrut(cleaned);
                  table.options.meta?.updateData(
                    index,
                    "slide_code",
                    composeSlideCode(original, cleaned),
                  );
                }}
              />
            </div>
            <span
              style={{
                whiteSpace: "nowrap",
                color: "#6c757d",
                fontSize: 13,
              }}
            >
              {suffix}
            </span>
          </div>
        );
      },
      size: 320,
    },
    {
      header: "Dikirimkan untuk uji silang ke kab/kota",
      accessorKey: "is_uji_silang_kabkota",
      cell: ({ row: { index, original }, table }) => {
        return (
          <InputRadio
            name={`is_uji_silang_kabkota_${index}`}
            options={YesAndNoOptions}
            selectedOption={original?.is_uji_silang_kabkota}
            disabled={!original?.isEdit}
            onChange={(e) => {
              table.options.meta?.updateData(
                index,
                "is_uji_silang_kabkota",
                e.target.value,
              );

              if (e.target.value === "Tidak") {
                table.options.meta?.updateData(
                  index,
                  "tgl_slide_dikirim",
                  "",
                );
                table.options.meta?.updateData(
                  index,
                  "petugas_mikroskopis",
                  "",
                );
              }
            }}
          />
        );
      },
      size: 250,
    },
    {
      header: "Tanggal slide dikirim",
      accessorKey: "tgl_slide_dikirim",
      cell: ({ row: { index, original }, table }) => {
        if (original?.is_uji_silang_kabkota === "Tidak") return "";
        if (!original?.isEdit) return original.tgl_slide_dikirim || "";
        return (
          <InputDate
            placeholder={"Pilih tanggal"}
            name={"tgl_slide_dikirim"}
            dateFormat={"YYYY-MM-DD"}
            value={original.tgl_slide_dikirim ?? ""}
            onChange={(e) => {
              const selectedDate = e ? moment(e).format("YYYY-MM-DD") : "";
              table.options.meta?.updateData(
                index,
                "tgl_slide_dikirim",
                selectedDate,
              );
            }}
          />
        );
      },
      size: 250,
    },
    {
      header: "Petugas mikroskopis",
      accessorKey: "petugas_mikroskopis",
      cell: ({ row: { index, original }, table }) => {
        if (original?.is_uji_silang_kabkota === "Tidak") return "";
        return (
          <InputSelect
            name={"petugas_mikroskopis"}
            placeholder={"Pilih petugas"}
            data={petugasOptions}
            disabled={
              original?.is_uji_silang_kabkota === "Tidak" || !original?.isEdit
            }
            onChange={(e) => {
              const selectedValue = e ? e.value : "";
              table.options.meta?.updateData(
                index,
                "petugas_mikroskopis",
                selectedValue,
              );
            }}
            value={petugasOptions.find(
              (o) => o.value === original.petugas_mikroskopis,
            )}
          />
        );
      },
      size: 250,
    },
  ];

  const tableInstance = useReactTable({
    columns,
    data: dataState?.data,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      pagination: {
        pageSize: dataState.limit,
        pageIndex: dataState.currentPage - 1,
      },
    },
    meta: {
      updateData: (rowIndex, field, value, rowData) => {
        setDataState((prevState) => {
          const newData = [...prevState.data];
          if (rowData) newData[rowIndex] = rowData;
          newData[rowIndex][field] = value;
          return { ...prevState, data: newData };
        });
      },
    },
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <h1 className="page-title">Uji Silang Fasyankes - Slide Negatif</h1>
      {/* Filter section start */}
      <div className="card-filter">
        <div className="filter-item">
          <InputDate
            placeholder={"Pilih tahun"}
            name={"filter_year"}
            dateFormat={"YYYY"}
            onChange={(e) => {
              const selectedYear = moment(e).format("YYYY");
              setFilterState({
                ...filterState,
                filter_year: selectedYear,
              });
            }}
            value={filterState.filter_year}
          />
        </div>
        <div className="filter-item">
          <InputSelect
            placeholder={"Pilih bulan"}
            data={MonthOptions}
            onChange={(e) => {
              const selectedValue = e ? e.value : "";
              setFilterState({
                ...filterState,
                filter_month: selectedValue,
              });
            }}
            value={MonthOptions.find(
              (item) => item.value === filterState.filter_month,
            )}
          />
        </div>
        <div className="filter-item">
          <InputField
            placeholder="Masukan nama"
            value={filterState.filter_name}
            onChange={(e) => {
              setFilterState({
                ...filterState,
                filter_name: e.target.value,
              });
            }}
            name="filter_name"
          />
        </div>
        <Button
          variant={"primary"}
          size={"normal"}
          onClick={() => fetchInputData()}
        >
          Terapkan
        </Button>
      </div>
      {/* Filter section end */}

      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "end",
          gap: 10,
        }}
      >
        <InputField
          label={
            "Berapa jumlah sediaan darah negatif yang dikirim untuk uji silang?"
          }
          isRequired
          name={"jumlah_sd"}
          type="number"
          specifiedType="number"
          min={1}
          value={jumlahSd}
          onChange={(e) => setJumlahSd(+(e.target.value ?? "0"))}
          disabled={!filterState.filter_year || !filterState.filter_month}
        />
        <Button
          variant={"primary"}
          size={"normal"}
          onClick={() => generateData()}
          isDisabled={!filterState.filter_year || !filterState.filter_month}
        >
          Tambahkan
        </Button>
      </div>

      <TableDataComponent
        columnLength={columns?.length}
        tableInstance={tableInstance}
        onPageChange={onPageChange}
        loading={isLoading}
        currentPage={dataState?.currentPage}
        limit={dataState?.limit}
        nextPage={dataState?.nextPage}
        previousPage={dataState?.previousPage}
        total={dataState?.totalData}
      />
    </div>
  );
};

export default Table;
